import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import './SessionLog.css';
import './VehicleLog.css';
import './Disputes.css';
import { api } from '../config/api';
import { getAuthData } from '../utils/auth';

interface ViolationResponse {
  violationTrackerId: string;
  plateNumber: string;
  lotId: string;
  sessionId: string | null;
  vehicleId: string | null;
  violationType: string;
  violationTimestamp: string;
  vehicleMake: string | null;
  vehicleModel: string | null;
  vehicleColor: string | null;
  totalViolationAmount: string | number | null;
  violationStatus: string;
  evidenceURL: string | null;
  recordedBy: string | null;
  recordedOn: string | null;
  initialNotes: string | null;
  resolvedBy: string | null;
  resolvedOn: string | null;
  resolutionNotes: string | null;
  dismissedBy: string | null;
  dismissedOn: string | null;
  dismissalNotes: string | null;
  disputedBy: string | null;
  disputedOn: string | null;
  disputeNotes: string | null;
}

interface GenericResponse<T> {
  success: boolean;
  data: T;
  message: string;
  status: number;
}

interface ParsedDisputeNotes {
  name: string | null;
  email: string | null;
  reason: string | null;
}

const parseDisputeNotes = (notes: string | null): ParsedDisputeNotes => {
  if (!notes) {
    return { name: null, email: null, reason: null };
  }

  const normalized = notes.replace(/\r?\n/g, ' ');
  const nameMatch = normalized.match(/Name:\s*([^,]+)/i);
  const emailMatch = normalized.match(/Email:\s*([^,]+)/i);
  const reasonMatch = normalized.match(/Reason:\s*(.*)/i);

  const name = nameMatch ? nameMatch[1].trim() : null;
  const email = emailMatch ? emailMatch[1].trim() : null;
  const reason = reasonMatch ? reasonMatch[1].trim() : null;

  return {
    name: name && name.length > 0 ? name : null,
    email: email && email.length > 0 ? email : null,
    reason: reason && reason.length > 0 ? reason : null,
  };
};

const Disputes: React.FC = () => {
  const { lotId } = useParams<{ lotId: string }>();
  const [disputes, setDisputes] = useState<ViolationResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDispute, setSelectedDispute] = useState<ViolationResponse | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<'ACCEPT' | 'DENY' | null>(null);

  const loadDisputedViolations = useCallback(async (activeLotId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get<GenericResponse<ViolationResponse[]>>(
        `/violations/get-disputed-violations-by-lot`,
        {
          params: { lotId: activeLotId },
        }
      );

      const responseData = response.data;
      if (responseData.success && Array.isArray(responseData.data)) {
        setDisputes(responseData.data);
      } else {
        setDisputes([]);
      }
    } catch (fetchError) {
      console.error('Failed to load disputed violations', fetchError);
      setError('Unable to load disputed violations. Please try again later.');
      setDisputes([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!lotId) {
      return;
    }

    loadDisputedViolations(lotId);
  }, [lotId, loadDisputedViolations]);

  const filteredDisputes = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return disputes;
    }

    return disputes.filter((dispute) => {
      return [
        dispute.plateNumber,
        dispute.violationType,
        dispute.violationTimestamp,
        dispute.vehicleMake,
        dispute.vehicleModel,
        dispute.vehicleColor,
      ]
        .filter(Boolean)
        .some((value) => value!.toString().toLowerCase().includes(query));
    });
  }, [disputes, searchQuery]);

  const handleRowClick = (dispute: ViolationResponse) => {
    setSelectedDispute(dispute);
    setActionError(null);
  };

  const closeModal = () => setSelectedDispute(null);

  const formatTimestamp = (value: string | null) => {
    if (!value) {
      return '—';
    }

    try {
      return new Date(value).toLocaleString();
    } catch (e) {
      return value;
    }
  };

  const formatViolationType = (value: string) => {
    return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const formatAmount = (value: ViolationResponse['totalViolationAmount']) => {
    if (value === null || value === undefined) {
      return '—';
    }

    const numericValue = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(numericValue)) {
      return '—';
    }

    return `$${numericValue.toFixed(2)}`;
  };

  const renderEvidenceLinks = (value: string | null) => {
    if (!value) {
      return <span className="detail-value">No evidence provided.</span>;
    }

    const urls = value.split(/\s+/).filter(Boolean);

    if (!urls.length) {
      return <span className="detail-value">No evidence provided.</span>;
    }

    return (
      <div className="detail-value" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {urls.map((url, index) => (
          <a key={`${url}-${index}`} href={url} target="_blank" rel="noreferrer">
            View evidence {urls.length > 1 ? index + 1 : ''}
          </a>
        ))}
      </div>
    );
  };

  const getUpdatedBy = () => {
    const authData = getAuthData();
    return authData?.userId || 'superadmin';
  };

  const handleStatusChange = async (status: 'RESOLVED' | 'OPEN') => {
    if (!selectedDispute) {
      return;
    }

    setIsUpdatingStatus(true);
    setActionError(null);
    setPendingAction(status === 'RESOLVED' ? 'ACCEPT' : 'DENY');

    const params = new URLSearchParams({
      violationId: selectedDispute.violationTrackerId,
      updatedBy: getUpdatedBy(),
      violationStatus: status,
    });

    try {
      await api.patch(`/violations/update-single-violation?${params.toString()}`);
      setSelectedDispute(null);
      if (lotId) {
        await loadDisputedViolations(lotId);
      }
    } catch (updateError: any) {
      console.error('Failed to update disputed violation status', updateError);
      const message = updateError?.response?.data?.message || 'Unable to update dispute. Please try again later.';
      setActionError(message);
    } finally {
      setIsUpdatingStatus(false);
      setPendingAction(null);
    }
  };

  const parsedDisputeNotes = useMemo(() => {
    if (!selectedDispute) {
      return null;
    }
    return parseDisputeNotes(selectedDispute.disputeNotes);
  }, [selectedDispute]);

  return (
    <div className={`session-log ${filteredDisputes.length > 0 ? 'has-data' : ''}`}>
      <div className="title-section">
        <h1>Disputes</h1>
      </div>

      <div className="search-and-download">
        <div className="search-bar">
          <img src="/assets/SearchBarIcon.svg" alt="Search" />
          <input
            type="text"
            value={searchQuery}
            placeholder="Search by plate, type, or vehicle details"
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </div>
      </div>

      {filteredDisputes.length > 0 && (
        <table className="session-log-table has-data">
          <thead>
            <tr>
              <th>
                <div className="transaction-log-header-content">
                  <span className="transaction-log-header-text">Plate</span>
                </div>
              </th>
              <th>
                <div className="transaction-log-header-content">
                  <span className="transaction-log-header-text">Violation Type</span>
                </div>
              </th>
              <th>
                <div className="transaction-log-header-content">
                  <span className="transaction-log-header-text">Violation Time</span>
                </div>
              </th>
              <th>
                <div className="transaction-log-header-content">
                  <span className="transaction-log-header-text">Vehicle</span>
                </div>
              </th>
              <th>
                <div className="transaction-log-header-content">
                  <span className="transaction-log-header-text">Amount</span>
                </div>
              </th>
              <th>
                <div className="transaction-log-header-content">
                  <span className="transaction-log-header-text">Disputed On</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredDisputes.map((dispute) => (
              <tr
                key={dispute.violationTrackerId}
                onClick={() => handleRowClick(dispute)}
                className="clickable-row"
              >
                <td>{dispute.plateNumber}</td>
                <td>{formatViolationType(dispute.violationType)}</td>
                <td>{formatTimestamp(dispute.violationTimestamp)}</td>
                <td>
                  {[dispute.vehicleMake, dispute.vehicleModel, dispute.vehicleColor]
                    .filter(Boolean)
                    .join(' ') || '—'}
                </td>
                <td>{formatAmount(dispute.totalViolationAmount)}</td>
                <td>{formatTimestamp(dispute.disputedOn)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {isLoading && <div className="loading-indicator">Loading disputed violations…</div>}

      {!isLoading && error && (
        <div className="no-data-container">
          <h3 className="no-results-header">Unable to load disputed violations.</h3>
          <p>{error}</p>
        </div>
      )}

      {!isLoading && !error && filteredDisputes.length === 0 && (
        <div className="no-data-container">
          <h3 className="no-results-header">No Disputes Found</h3>
          <p>There are no disputed violations for this lot.</p>
        </div>
      )}

      {selectedDispute && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="info-modal-content detail-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Dispute Details</h2>
              <button className="close-button" onClick={closeModal} aria-label="Close">
                ×
              </button>
            </div>

            <div className="dispute-modal-section">
              <h3>Violation</h3>
              <div className="dispute-modal-columns">
                <div className="dispute-column">
                  <div className="detail-item">
                    <span className="detail-label">Plate Number</span>
                    <span className="detail-value">{selectedDispute.plateNumber}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Violation Type</span>
                    <span className="detail-value">{formatViolationType(selectedDispute.violationType)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Violation Time</span>
                    <span className="detail-value">{formatTimestamp(selectedDispute.violationTimestamp)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Total Amount</span>
                    <span className="detail-value">{formatAmount(selectedDispute.totalViolationAmount)}</span>
                  </div>
                </div>
                <div className="dispute-column">
                  <div className="detail-item">
                    <span className="detail-label">Status</span>
                    <span className="detail-value">{formatViolationType(selectedDispute.violationStatus)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Recorded By</span>
                    <span className="detail-value">{selectedDispute.recordedBy || '—'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Recorded On</span>
                    <span className="detail-value">{formatTimestamp(selectedDispute.recordedOn)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Vehicle</span>
                    <span className="detail-value">
                      {[selectedDispute.vehicleColor, selectedDispute.vehicleMake, selectedDispute.vehicleModel]
                        .filter(Boolean)
                        .join(' ') || '—'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="detail-item detail-block">
                <span className="detail-label">Initial Notes</span>
                <span className="detail-value multiline">{selectedDispute.initialNotes || '—'}</span>
              </div>
            </div>

            <div className="dispute-modal-section">
              <h3>Dispute</h3>
              <div className="dispute-modal-columns">
                <div className="dispute-column">
                  <div className="detail-item">
                    <span className="detail-label">Name</span>
                    <span className="detail-value">{parsedDisputeNotes?.name || selectedDispute.disputedBy || '—'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Email</span>
                    <span className="detail-value">{parsedDisputeNotes?.email || '—'}</span>
                  </div>
                </div>
                <div className="dispute-column">
                  <div className="detail-item">
                    <span className="detail-label">Disputed By (User)</span>
                    <span className="detail-value">{selectedDispute.disputedBy || '—'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Disputed On</span>
                    <span className="detail-value">{formatTimestamp(selectedDispute.disputedOn)}</span>
                  </div>
                </div>
              </div>
              <div className="dispute-reason">
                <span className="detail-label">Reason</span>
                <span className="detail-value multiline">{parsedDisputeNotes?.reason || selectedDispute.disputeNotes || '—'}</span>
              </div>

              <div className="dispute-evidence">
                <span className="detail-label">Evidence</span>
                <div className="detail-item">
                  {renderEvidenceLinks(selectedDispute.evidenceURL)}
                </div>
              </div>
            </div>

            {actionError && (
              <div className="dispute-error">
                {actionError}
              </div>
            )}

            <div className="dispute-actions">
              <button
                type="button"
                className="dispute-action-button accept"
                onClick={() => handleStatusChange('RESOLVED')}
                disabled={isUpdatingStatus}
              >
                {pendingAction === 'ACCEPT' && isUpdatingStatus ? 'Updating...' : 'Accept'}
              </button>
              <button
                type="button"
                className="dispute-action-button deny"
                onClick={() => handleStatusChange('OPEN')}
                disabled={isUpdatingStatus}
              >
                {pendingAction === 'DENY' && isUpdatingStatus ? 'Updating...' : 'Deny'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Disputes;
